"use client";

import { useState } from "react";
import Link from "next/link";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type {
  CPhotoGalleryContent,
  CPhotoGalleryGallery,
  CPhotoGalleryImage,
  CPhotoGalleryItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import {
  cPhotoGalleryTranslations,
  type CPhotoGalleryTranslations,
} from "./CPhotoGallery.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CPhotoGallery.module.css";

/**
 * CPhotoGallery - visitor view komponentu `c_photo_gallery` (Galeria zdjęć).
 *
 * **Data source:** `C_PHOTO_GALLERY_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   list:    { id, symbol, view:'list', items:[{ id, name, description,
 *              count_pictures, picture, href }], pagination }
 *   gallery: { id, symbol, view:'gallery', gallery:{ id, name, description,
 *              images:[{ id, thumb_src, full_src, ... }], pagination },
 *              path, list_href }
 *   ```
 *
 * **Dwa tryby (discriminator `content.view`):**
 *   - `list` - siatka galerii; każdy kafel linkuje do widoku galerii (`item.href`).
 *     URL galerii niesie article_id (z `parseSlug.articles[0]`), backend mapuje go
 *     na `gallery_id` (alias) i zwraca gallery view.
 *   - `gallery` - pojedyncza galeria: siatka miniatur otwierających lightbox
 *     (`yet-another-react-lightbox`) na pełnym obrazie + powrót do listy.
 *
 * **Lightbox source:** kafel pokazuje `thumb_src` (miniatura `art_thumb`),
 * lightbox slajd pokazuje `full_src` (pełen obraz). Wzorzec skopiowany z
 * `components/cms/Paragraphs/shared/ImageGallery.tsx`.
 */
export function CPhotoGallery({ content }: CPhotoGalleryProps) {
  const t = useT(cPhotoGalleryTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "gallery" && content.gallery ? (
        <GalleryView
          gallery={content.gallery}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CPhotoGalleryProps = {
  content: CPhotoGalleryContent;
};

/* ─── List view (siatka galerii) ────────────────────────────────────────── */

function ListView({
  content,
  t,
}: {
  content: CPhotoGalleryContent;
  t: CPhotoGalleryTranslations;
}) {
  const items: CPhotoGalleryItem[] = Array.isArray(content.items)
    ? content.items
    : [];
  const pagination = content.pagination;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <CatalogLoadMore<CPhotoGalleryItem>
      initialItems={items}
      initialPage={pagination?.page ?? 1}
      totalPages={pagination?.total_pages ?? 1}
      totalCount={pagination?.total_count}
      getKey={(item) => item.id}
      extractItems={(c) => {
        const arr = (c as CPhotoGalleryContent | null)?.items;
        return Array.isArray(arr) ? arr : [];
      }}
    >
      {(allItems) => (
        <ul className={styles.galleries}>
          {allItems.map((item) => (
            <GalleryCard key={item.id} item={item} t={t} />
          ))}
        </ul>
      )}
    </CatalogLoadMore>
  );
}

function GalleryCard({
  item,
  t,
}: {
  item: CPhotoGalleryItem;
  t: CPhotoGalleryTranslations;
}) {
  const href = extractInternalHref(item.href);

  const inner = (
    <>
      <div className={styles.coverWrap}>
        {item.picture ? (
          /* Native <img> - `picture.thumb_src` to URL plików z Imperial
             backend'u (`/data/domains/.../images/s_okladka.jpg`). Bez Next/Image
             bo dynamic source + cross-origin (backend = inna domena niż React-CMS). */
          <img
            src={item.picture.thumb_src}
            alt={item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.cover}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className={styles.coverPlaceholder} aria-hidden="true" />
        )}
        {item.count_pictures > 0 && (
          <span className={styles.countBadge}>
            {t.photosCount(item.count_pictures)}
          </span>
        )}
      </div>
      <div className={styles.galleryCardBody}>
        <h2 className={styles.galleryName}>{item.name}</h2>
        {item.description && (
          <p className={styles.galleryDesc}>{item.description}</p>
        )}
      </div>
    </>
  );

  return (
    <li className={styles.galleryCard}>
      {href !== null ? (
        <Link href={href} className={styles.galleryCardLink} aria-label={item.name}>
          {inner}
        </Link>
      ) : (
        <a href={item.href} className={styles.galleryCardLink} aria-label={item.name}>
          {inner}
        </a>
      )}
    </li>
  );
}

/* ─── Gallery view (pojedyncza galeria + lightbox) ──────────────────────── */

function GalleryView({
  gallery,
  listHref,
  t,
}: {
  gallery: CPhotoGalleryGallery;
  listHref: string | null;
  t: CPhotoGalleryTranslations;
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);
  const images: CPhotoGalleryImage[] = Array.isArray(gallery.images)
    ? gallery.images
    : [];
  const pagination = gallery.pagination;
  const backHref = listHref ? extractInternalHref(listHref) : null;

  return (
    <div>
      {(backHref !== null || listHref) && (
        <div className={styles.galleryBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.galleryBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.galleryBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      {gallery.description && (
        <div className={styles.galleryLead}>{gallery.description}</div>
      )}

      {images.length === 0 ? (
        <div className={styles.empty}>{t.emptyGallery}</div>
      ) : (
        <CatalogLoadMore<CPhotoGalleryImage>
          initialItems={images}
          initialPage={pagination?.page ?? 1}
          totalPages={pagination?.total_pages ?? 1}
          totalCount={pagination?.total_count}
          getKey={(img) => img.id}
          extractItems={(c) => {
            const arr = (c as CPhotoGalleryContent | null)?.gallery?.images;
            return Array.isArray(arr) ? arr : [];
          }}
        >
          {(allImages) => {
            /* Lightbox slajdy z PEŁNEGO (doczytanego) zbioru - indeks z siatki
               pasuje do indeksu slajdu. */
            const slides = allImages.map((img) => ({
              src: img.full_src,
              width: img.full_width ?? img.width ?? undefined,
              height: img.full_height ?? img.height ?? undefined,
              alt: img.description || gallery.name,
              description: img.description || undefined,
            }));
            return (
              <>
                <div className={styles.photos}>
                  {allImages.map((img, i) => (
                    <button
                      key={img.id}
                      type="button"
                      className={styles.photoItem}
                      onClick={() => setOpenAt(i)}
                      aria-label={img.description || t.openImage}
                    >
                      <img
                        src={img.thumb_src}
                        alt={img.description || gallery.name}
                        width={img.width ?? undefined}
                        height={img.height ?? undefined}
                        loading="lazy"
                        decoding="async"
                      />
                      {img.description && (
                        <span className={styles.photoCaption}>{img.description}</span>
                      )}
                    </button>
                  ))}
                </div>

                <Lightbox
                  open={openAt !== null}
                  index={openAt ?? 0}
                  close={() => setOpenAt(null)}
                  slides={slides}
                />
              </>
            );
          }}
        </CatalogLoadMore>
      )}
    </div>
  );
}
